// src/pages/onboarding/search-terms.tsx
import React, { useState, useMemo, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useOnboardingData } from "@/contexts/onboarding-data-context";
import { useOnboarding } from "@/contexts/onboarding-context";
import { getOnboardingRedirectPath } from "@/lib/onboarding-navigation-utils";
import { LoadingSpinner } from "@/components/ui/loading";
import { SearchTermData } from "@/types/metrics";
import { DataDisplay } from "@/components/data-display";
import { SearchTable } from "@/components/search-table";
import { NgramTable } from "@/components/ngram-table";
import { processSearchTermNgrams } from "@/lib/ngram-utils";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Card, CardContent } from "@/components/ui/card";
import { TreeVisualization } from "@/components/tree-visualization";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";

interface ProcessedSearchTermData extends SearchTermData {
  CTR: number;
  CPC: number;
  CvR: number;
  ROAS: number;
  CPA: number;
  matchType: string;
}

interface SearchFilters {
  textFilters: {
    campaign: string;
    searchTerm: string;
  };
  matchTypes: Set<string>;
  thresholds: {
    impr: number;
    cost: number;
    conv: number;
  };
}

type SortDirection = "asc" | "desc";

const MATCH_TYPES = [
  "BROAD",
  "PHRASE",
  "EXACT",
  "NEAR_BROAD",
  "NEAR_PHRASE",
  "NEAR_EXACT",
];

export default function OnboardingSearchTermsPage() {
  const { data, loadingStatus, hasDataBeenFetched } = useOnboardingData();
  const { webAppUrl } = useOnboarding();
  const accountInfo = useMemo(
    () => (data?.account && data.account.length > 0 ? data.account[0] : null),
    [data],
  );
  const accountCurrency = accountInfo?.currency || "$";
  const scriptVersion = accountInfo?.scriptVersion || "N/A";

  const [displayType, setDisplayType] = useState<"tree" | "ngram" | "table">(
    "tree",
  );
  const [maxNameLength, setMaxNameLength] = useState(25);
  const [wordCount, setWordCount] = useState(1);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);

  const [sliderMaxValues, setSliderMaxValues] = useState({
    impression: 100,
    cost: 100,
  });
  const [isInitialFilterSet, setIsInitialFilterSet] = useState(false);

  const handleSliderChange = (
    value: number[],
    setter: (val: number) => void,
    max: number,
  ) => {
    const linearValue = value[0];
    if (max <= 1) {
      setter(Math.round((linearValue / 100) * max));
      return;
    }
    const logValue = Math.round(
      Math.pow(10, (linearValue / 100) * Math.log10(max)),
    );
    setter(logValue);
  };

  // Helper to get metric value at slider percentage (0-100)
  const sliderValueForPosition = (percentage: number, max: number) => {
    if (max <= 1) return Math.round((percentage / 100) * max);
    return Math.round(Math.pow(10, (percentage / 100) * Math.log10(max)));
  };

  const getSliderPosition = (value: number, max: number) => {
    if (value <= 0 || max <= 1) return 0;
    const position = (Math.log10(value) / Math.log10(max)) * 100;
    return Math.min(100, Math.max(0, position));
  };

  const collapseAll = useCallback(() => {
    setExpandedRows(new Set());
  }, []);

  // Initial filter state
  const [searchFilters, setSearchFilters] = useState<SearchFilters>({
    matchTypes: new Set(MATCH_TYPES),
    textFilters: {
      campaign: "",
      searchTerm: "",
    },
    thresholds: {
      impr: 0,
      cost: 0,
      conv: 0,
    },
  });

  // We'll reset pagination when filters change (effect defined later after filters state)

  const processedSearchData = useMemo(() => {
    if (!data) return [];
    const searchData = data.search80 || [];

    const processed = (searchData as any[]).map((row: any) => {
      // Normalise numeric fields to actual numbers
      const impr = Number(row.impr || 0);
      const clicks = Number(row.clicks || 0);
      const cost = Number(row.cost || 0);
      const conv = Number(row.conv || 0);
      const value = Number(row.value || 0);

      return {
        ...row,
        // Ensure required string fields are never undefined
        campaign: String(row.campaign || "Unknown Campaign"),
        searchTerm: String(row.searchTerm || "Unknown Search Term"),
        searchMatchType: String(row.searchMatchType || "EXACT"),
        impr,
        clicks,
        cost,
        conv,
        value,
        CTR: impr ? (clicks / impr) * 100 : 0,
        CPC: clicks ? cost / clicks : 0,
        CvR: clicks ? (conv / clicks) * 100 : 0,
        ROAS: cost ? value / cost : 0,
        CPA: conv ? cost / conv : 0,
        matchType: row.searchMatchType,
      };
    });
    return processed;
  }, [data]);

  const [sort, setSort] = useState<{ field: string; direction: SortDirection }>(
    {
      field: "cost",
      direction: "desc",
    },
  );

  const [searchVisibleColumns, setSearchVisibleColumns] = useState(
    new Set([
      "campaign",
      "searchMatchType",
      "searchTerm",
      "impr",
      "clicks",
      "CTR",
      "cost",
      "CPC",
      "conv",
      "value",
      "ROAS",
      "CPA",
    ]),
  );
  const [ngramVisibleColumns, setNgramVisibleColumns] = useState(
    new Set([
      "ngram",
      "count",
      "impr",
      "clicks",
      "CTR",
      "cost",
      "CPC",
      "conv",
      "CvR",
      "value",
      "ROAS",
    ]),
  );
  const [rowsPerPage, setRowsPerPage] = useState(30);

  const MAX_TREE_NODES = 250; // Limit tree size for performance

  const filteredData = useMemo(() => {
    if (!processedSearchData?.length) return [];

    console.log("[DEBUG] Filter state:", {
      matchTypesSize: searchFilters.matchTypes.size,
      matchTypes: Array.from(searchFilters.matchTypes),
      thresholds: searchFilters.thresholds,
      textFilters: searchFilters.textFilters,
    });

    const filtered = processedSearchData.filter((row) => {
      const { campaign, searchTerm } = searchFilters.textFilters;
      if (
        campaign &&
        !row.campaign.toLowerCase().includes(campaign.toLowerCase())
      )
        return false;
      if (
        searchTerm &&
        !row.searchTerm.toLowerCase().includes(searchTerm.toLowerCase())
      )
        return false;
      if (
        searchFilters.matchTypes.size > 0 &&
        !searchFilters.matchTypes.has(row.searchMatchType)
      ) {
        return false;
      }
      if (row.impr < searchFilters.thresholds.impr) return false;
      if (row.cost < searchFilters.thresholds.cost) return false;
      if (row.conv < searchFilters.thresholds.conv) return false;

      return true;
    });

    console.log(
      "[DEBUG] Filtered results:",
      filtered.length,
      "from",
      processedSearchData.length,
    );
    return filtered;
  }, [processedSearchData, searchFilters, displayType]);

  const ngramData = useMemo(() => {
    let processedNgrams = processSearchTermNgrams(
      filteredData as SearchTermData[],
      3,
      wordCount,
    );
    const sorted = [...processedNgrams].sort((a, b) => {
      const aValue = a[sort.field as keyof typeof a];
      const bValue = b[sort.field as keyof typeof b];
      const multiplier = sort.direction === "asc" ? 1 : -1;
      if (typeof aValue === "string" && typeof bValue === "string") {
        return multiplier * aValue.localeCompare(bValue);
      }
      return multiplier * ((Number(aValue) || 0) - (Number(bValue) || 0));
    });
    return sorted;
  }, [filteredData, sort, wordCount]);

  const filteredNgramData = useMemo(() => {
    if (displayType !== "ngram") return ngramData;
    return ngramData.filter((ngram) => {
      if (ngram.impr < searchFilters.thresholds.impr) return false;
      if (ngram.cost < searchFilters.thresholds.cost) return false;
      if (ngram.conv < searchFilters.thresholds.conv) return false;
      return true;
    });
  }, [ngramData, displayType, searchFilters]);

  useEffect(() => {
    if (displayType === "ngram") {
      setSort({ field: "impr", direction: "desc" });
    }
  }, [displayType]);

  const sortedData = useMemo(() => {
    if (displayType === "ngram") return filteredData;
    return [...filteredData].sort((a, b) => {
      const aValue = a[sort.field as keyof typeof a];
      const bValue = b[sort.field as keyof typeof b];
      const multiplier = sort.direction === "asc" ? 1 : -1;
      if (typeof aValue === "string" && typeof bValue === "string") {
        return multiplier * aValue.localeCompare(bValue);
      }
      return multiplier * ((Number(aValue) || 0) - (Number(bValue) || 0));
    });
  }, [filteredData, sort, displayType]);

  // Effect to set slider ranges and initial values once data is processed
  useEffect(() => {
    if (processedSearchData.length > 0 && !isInitialFilterSet) {
      const maxImpressions = Math.max(
        ...processedSearchData.map((k) => k.impr),
        0,
      );
      const maxCost = Math.max(...processedSearchData.map((k) => k.cost), 0);

      setSliderMaxValues({
        impression: maxImpressions > 0 ? maxImpressions : 100,
        cost: maxCost > 0 ? maxCost : 100,
      });

      setSearchFilters((prev) => ({
        ...prev,
        thresholds: {
          ...prev.thresholds,
          impr: sliderValueForPosition(
            10,
            maxImpressions > 0 ? maxImpressions : 100,
          ),
          cost: sliderValueForPosition(10, maxCost > 0 ? maxCost : 100),
          conv: 0,
        },
      }));
      setIsInitialFilterSet(true);
    }
  }, [processedSearchData, isInitialFilterSet]);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchFilters]);

  // DEBUG: log first row of each data source when data is available
  useEffect(() => {
    if (!data) {
      console.log(
        "[Onboarding Data Check: search-terms] Data is not yet available.",
      );
      return;
    }
    const logPrefix = "[Onboarding Data Check: search-terms]";
    console.log(
      `${logPrefix} Using search80 data with ${data.search80?.length || 0} rows. Sample:`,
      data.search80?.slice(0, 2),
    );
    console.log(
      `${logPrefix} Processed data length:`,
      processedSearchData.length,
    );
    console.log(
      `${logPrefix} Processed data sample:`,
      processedSearchData.slice(0, 2),
    );
    console.log(`${logPrefix} Filtered data length:`, filteredData.length);
    console.log(`${logPrefix} Sorted data length:`, sortedData.length);
  }, [data, processedSearchData, filteredData, sortedData]);

  const handleViewTypeChange = (
    type: "tree" | "table" | "ngram" | "insights",
  ) => {
    if (type === "insights") return; // ignore insights view in onboarding
    setDisplayType(type as "tree" | "table" | "ngram");
  };

  // By the time we get here, data should be loaded. If not, show a message.
  if (!hasDataBeenFetched || !data || !processedSearchData?.length) {
    const redirect = getOnboardingRedirectPath(webAppUrl, !!data);

    return (
      <main className="flex-1 flex items-center justify-center p-4">
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>No Data Available</AlertTitle>
          <AlertDescription className="space-y-4">
            <p>{redirect.message}</p>
            <Button asChild>
              <Link to={redirect.path}>Continue</Link>
            </Button>
          </AlertDescription>
        </Alert>
      </main>
    );
  }

  return (
    <main className="flex-1 p-4 lg:p-6 space-y-4">
      {/* Filter Section */}
      <Card className="mb-4 w-full">
        <CardContent className="space-y-2 p-4">
          <h2 className="text-xl font-semibold">Filter Search Terms</h2>
          <p className="text-muted-foreground text-sm">
            For now just the most important terms are shown, we'll load more
            later. Use the controls below to filter the data shown. Feel free to
            explore the Table and nGram views too.
          </p>
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[160px]">
              <Label htmlFor="campaign-filter">Campaign includes</Label>
              <Input
                id="campaign-filter"
                placeholder="Filter by campaign..."
                value={searchFilters.textFilters.campaign}
                onChange={(e) =>
                  setSearchFilters((prev) => ({
                    ...prev,
                    textFilters: {
                      ...prev.textFilters,
                      campaign: e.target.value,
                    },
                  }))
                }
              />
            </div>
            <div className="flex-1 min-w-[160px]">
              <Label htmlFor="search-term-filter">Search Term includes</Label>
              <Input
                id="search-term-filter"
                placeholder="Filter by search term..."
                value={searchFilters.textFilters.searchTerm}
                onChange={(e) =>
                  setSearchFilters((prev) => ({
                    ...prev,
                    textFilters: {
                      ...prev.textFilters,
                      searchTerm: e.target.value,
                    },
                  }))
                }
              />
            </div>
            <div className="flex-1 min-w-[200px]">
              <Label>
                Impr {">="} {searchFilters.thresholds.impr}
              </Label>
              <Slider
                value={[
                  getSliderPosition(
                    searchFilters.thresholds.impr,
                    sliderMaxValues.impression,
                  ),
                ]}
                onValueChange={(value) =>
                  handleSliderChange(
                    value,
                    (val) =>
                      setSearchFilters((p) => ({
                        ...p,
                        thresholds: { ...p.thresholds, impr: val },
                      })),
                    sliderMaxValues.impression,
                  )
                }
                max={100}
                step={1}
              />
            </div>
            <div className="flex-1 min-w-[200px]">
              <Label>
                Cost {">="} {accountCurrency}
                {searchFilters.thresholds.cost}
              </Label>
              <Slider
                value={[
                  getSliderPosition(
                    searchFilters.thresholds.cost,
                    sliderMaxValues.cost,
                  ),
                ]}
                onValueChange={(value) =>
                  handleSliderChange(
                    value,
                    (val) =>
                      setSearchFilters((p) => ({
                        ...p,
                        thresholds: { ...p.thresholds, cost: val },
                      })),
                    sliderMaxValues.cost,
                  )
                }
                max={100}
                step={1}
              />
            </div>
            <div className="flex-1 min-w-[140px]">
              <Label>
                Conv {">="} {searchFilters.thresholds.conv}
              </Label>
              <Slider
                value={[searchFilters.thresholds.conv]}
                onValueChange={([value]) =>
                  setSearchFilters((prev) => ({
                    ...prev,
                    thresholds: { ...prev.thresholds, conv: value },
                  }))
                }
                max={10}
                step={1}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="bg-background rounded-lg mb-4">
        <DataDisplay
          viewType={displayType}
          onViewTypeChange={handleViewTypeChange}
          pageContextType={"keywords"}
          data={displayType === "ngram" ? filteredNgramData : sortedData}
          treeComponent={(props) => (
            <>
              {sortedData.length > MAX_TREE_NODES && (
                <Alert className="mb-2">
                  <AlertDescription>
                    Showing top {MAX_TREE_NODES} search terms by impressions for
                    performance. We'll add more later.
                  </AlertDescription>
                </Alert>
              )}
              <TreeVisualization
                data={[...sortedData]
                  .sort((a, b) => b.impr - a.impr)
                  .slice(0, MAX_TREE_NODES)}
                type={"searchTerms"}
                maxLength={maxNameLength}
                onMaxLengthChange={setMaxNameLength}
                {...props}
              />
            </>
          )}
          tableComponent={
            <SearchTable
              data={sortedData as SearchTermData[]}
              visibleColumns={searchVisibleColumns}
              sort={sort}
              onSortChange={(field) => {
                setSort((prev) => ({
                  field,
                  direction:
                    prev.field === field
                      ? prev.direction === "asc"
                        ? "desc"
                        : "asc"
                      : "desc",
                }));
              }}
              currentPage={currentPage}
              rowsPerPage={rowsPerPage}
              onPageChange={setCurrentPage}
              accountCurrency={accountCurrency}
            />
          }
          ngramComponent={
            <NgramTable
              data={filteredNgramData}
              visibleColumns={ngramVisibleColumns}
              sort={sort}
              onSortChange={(field) => {
                setSort((prev) => ({
                  field,
                  direction:
                    prev.field === field
                      ? prev.direction === "asc"
                        ? "desc"
                        : "asc"
                      : "desc",
                }));
              }}
              currentPage={currentPage}
              rowsPerPage={rowsPerPage}
              onPageChange={setCurrentPage}
              accountCurrency={accountCurrency}
              type={"searchTerms"}
              expandedRows={expandedRows}
              setExpandedRows={setExpandedRows}
            />
          }
          onColumnsChange={
            displayType === "ngram"
              ? setNgramVisibleColumns
              : setSearchVisibleColumns
          }
          visibleColumns={
            displayType === "ngram" ? ngramVisibleColumns : searchVisibleColumns
          }
          onRowsPerPageChange={setRowsPerPage}
          rowsPerPage={rowsPerPage}
          type={"searchTerms"}
          wordCount={wordCount}
          onWordCountChange={setWordCount}
          hasExpandedRows={expandedRows?.size > 0}
          onCollapseAll={collapseAll}
          maxLength={maxNameLength}
          onMaxLengthChange={setMaxNameLength}
          hideShowKeywords
        />
      </div>
      {/* Navigation to next step */}
      <div className="text-center pt-8 mt-8 border-t">
        <Link to="/onboarding/ai-setup">
          <Button className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3">
            Next: Get AI Insights
          </Button>
        </Link>
      </div>
    </main>
  );
}
